import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";
import PublicBottomNav from "../components/PublicBottomNav";
import { Helmet } from "react-helmet-async";
import { Button } from "../components/ui/button";
import JsonLd from "../components/JsonLd";
import AwardBanner from "../components/AwardBanner";
import { ROOMS, TAX_NOTE, WHATSAPP, LAST_MINUTE, formatPrice } from "../data/rooms";
import { 
  MapPin, 
  Wifi, 
  Mountain, 
  Home,
  Users,
  Check,
  ChevronRight,
  ChevronLeft,
  Bed,
  Bath,
  Sun,
  Wind,
  Lock,
  Sparkles,
  HelpCircle,
  Mic,
  MessageCircle
} from "lucide-react";
import { pic } from "../lib/pic";

// Image Carousel Component
const ImageCarousel = ({ images, name, badge }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const touch = useRef(null);
  const onTouchStart = (e) => { touch.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touch.current === null || images.length < 2) return;
    const dx = e.changedTouches[0].clientX - touch.current;
    touch.current = null;
    if (Math.abs(dx) < 40) return;
    setCurrentIndex((prev) => (dx < 0 ? (prev + 1) % images.length : (prev - 1 + images.length) % images.length));
  };

  return (
    <div className="relative group" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="aspect-[4/3] overflow-hidden bg-[#2E2A26]/5">
        <img loading="lazy" decoding="async" 
          src={images[currentIndex]}
          alt={`${name} - Image ${currentIndex + 1}`}
          className="w-full h-full object-cover transition-transform duration-300"
        />
      </div>
      
      {/* Navigation Arrows: always visible on touch screens, hover-only on desktop */}
      {images.length > 1 && (
        <>
          <button
            onClick={prevImage}
            aria-label="Previous photo"
            className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 md:w-8 md:h-8 rounded-full bg-[#FBF7F1]/90 shadow-lg flex items-center justify-center md:opacity-0 md:group-hover:opacity-100 transition-opacity hover:bg-[#FBF7F1]"
          >
            <ChevronLeft className="w-5 h-5 text-[#2E2A26]" />
          </button>
          <button
            onClick={nextImage}
            aria-label="Next photo"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 md:w-8 md:h-8 rounded-full bg-[#FBF7F1]/90 shadow-lg flex items-center justify-center md:opacity-0 md:group-hover:opacity-100 transition-opacity hover:bg-[#FBF7F1]"
          >
            <ChevronRight className="w-5 h-5 text-[#2E2A26]" />
          </button>
        </>
      )}
      
      {/* Dots Indicator */}
      {images.length > 1 && (
        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex">
          {images.map((_, idx) => (
            <button
              key={idx}
              aria-label={`Photo ${idx + 1}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCurrentIndex(idx);
              }}
              className="p-2"
            >
              <span className={`block h-2 rounded-full transition-all ${idx === currentIndex ? 'bg-[#FBF7F1] w-4' : 'bg-[#FBF7F1]/60 w-2'}`} />
            </button>
          ))}
        </div>
      )}
      
      {/* Badge */}
      {badge && (
        <div className="absolute top-3 sm:top-4 left-3 sm:left-4 bg-[#C85A38] text-white text-xs px-3 py-1 rounded-full font-medium shadow-md">
          {badge}
        </div>
      )}
    </div>
  );
};

const RoomsPage = () => {
  const rooms = ROOMS;

  const amenities = [
    { icon: Bath, text: "Private bathroom (most rooms)" },
    { icon: Sun, text: "Natural light & views" },
    { icon: Wifi, text: "Ultra-fast Starlink WiFi" },
    { icon: Sparkles, text: "Weekly cleaning service" },
    { icon: Bed, text: "Sheets, pillows & fresh towels" },
    { icon: Lock, text: "Lockable door for privacy" },
    { icon: Wind, text: "Hairdryer, hangers & essentials" },
  ];

  const roomsSchemaData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Rooms at Savi Coliving",
    "itemListElement": rooms.map((room, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "item": {
        "@type": "Accommodation",
        "name": room.name,
        "description": room.description,
        "image": room.images[0],
        "amenityFeature": room.features.map(f => ({ "@type": "LocationFeatureSpecification", "name": f })),
        "offers": {
          "@type": "Offer",
          "price": String(room.price),
          "priceCurrency": "EUR",
          "availability": "https://schema.org/LimitedAvailability",
          "url": "https://www.savicoliving.com/rooms"
        }
      }
    }))
  };

  return (
    <div className="min-h-screen bg-[#FBF7F1]">
      <Helmet>
        <title>Rooms & Pricing | Savi Coliving Valencia</title>
        <meta name="description" content="Rooms at Savi Coliving in Valencia, Spain. Private rooms from €1,200/month with Starlink WiFi, pool, coworking space & mountain views." />
        <meta property="og:title" content="Rooms & Pricing | Savi Coliving" />
        <meta property="og:description" content="Private rooms from €1,200/month. Garden rooms, Sky rooms, Panorama suites & apartments available." />
        <meta property="og:image" content="https://static.wixstatic.com/media/447ef7_c4f040f251774723aaf22ea2f5874e83~mv2.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://www.savicoliving.com/rooms" />
      </Helmet>
      <JsonLd id="rooms" data={roomsSchemaData} />
      {/* Navigation */}
      <PublicNav />

      {/* Mobile Navigation */}
      <PublicBottomNav />

      {/* Hero */}
      <header className="relative pt-16">
        <div className="h-[40vh] sm:h-[50vh] relative overflow-hidden">
          <img fetchpriority="high" decoding="async" 
            {...pic("villa-front")}
            alt="Savi Rooms"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FBF7F1] from-15% via-[#FBF7F1]/75 via-50% to-black/20"></div>
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-8">
            <div className="max-w-6xl mx-auto">
              <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#2E2A26] mb-2">Our Rooms</h1>
              <p className="text-[#2E2A26]/70 text-sm sm:text-base max-w-2xl">
                Sixteen private rooms in seven styles — from a quiet garden single to an apartment with its own kitchen. Every one has light, a view and a proper bed; every rate includes the whole house.
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Award nomination banner */}
      <AwardBanner testid="rooms-award-banner" />

      {/* Last-minute opening */}
      <a
        href={LAST_MINUTE.whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block bg-[#C85A38] hover:bg-[#B34E2E] transition-colors text-white py-3 px-4 mt-2"
        data-testid="rooms-last-minute-banner"
      >
        <div className="max-w-6xl mx-auto flex items-center justify-center gap-2.5 text-center">
          <span className="relative flex h-3 w-3 flex-shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
          </span>
          <p className="text-sm sm:text-base font-medium">
            <span className="font-bold">Last minute:</span> {LAST_MINUTE.line}. <span className="underline underline-offset-4 decoration-white/60">Book now →</span>
          </p>
        </div>
      </a>

      {/* Last-minute Garden Room stays */}
      <div className="bg-[#F4ECE2] py-6 px-4" data-testid="garden-lastminute-strip">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[#2E2A26] text-sm sm:text-base mb-4">
            <span className="font-semibold">Need something sooner?</span> Now and then a two-week window opens in one of our Garden Rooms — these never make it to the site. Message Steve and ask.
          </p>
          <a
            href="https://wa.me/34632628051?text=Hi%20Steve!%20Are%20there%20any%20last-minute%20Garden%20Room%20stays%20available%3F"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="rooms-garden-whatsapp-cta"
            className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb958] text-white rounded-full px-7 py-3 text-sm font-medium transition-colors"
          >
            <MessageCircle className="w-4 h-4" /> Message Steve on WhatsApp
          </a>
        </div>
      </div>

      {/* WHAT EVERY RATE INCLUDES */}
      <section className="py-8 px-4 bg-[#FBF7F1]" data-testid="rooms-included-strip">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#C85A38] font-label text-xs tracking-[0.2em] uppercase mb-3">Every rate includes</p>
          <div className="flex flex-wrap justify-center gap-2 mb-4">
            {["Coworking desk with dual screens", "Starlink WiFi", "Pool, gym & 200 m² sky terrace", "Yoga, meditation & sound therapy", "Guided sierra hikes", "Weekly cleaning & fresh linen", "Utilities", "The weekly programme & guest app", "Two resident dogs"].map((item, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 bg-white border border-stone-200 text-[#2E2A26]/70 text-xs px-3 py-1.5 rounded-full" data-testid={`env-chip-${i}`}>
                <Check className="w-3 h-3 text-[#8C9A7C]" />
                {item}
              </span>
            ))}
          </div>
          <p className="text-[#2E2A26]/50 text-xs max-w-2xl mx-auto" data-testid="rooms-tax-note">{TAX_NOTE}</p>
          <p className="text-[#2E2A26]/60 text-xs max-w-2xl mx-auto mt-3" data-testid="rooms-transport-note">
            <span className="font-medium text-[#2E2A26]/80">Good to know:</span> we're five minutes' drive from Náquera, so most guests bring or hire a car or scooter. There's no shopping shuttle — if you come without a vehicle, plan on food delivery to the house.
          </p>
        </div>
      </section>

      {/* Rooms Grid */}
      <section className="py-10 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="space-y-8 sm:space-y-12">
            {rooms.map((room, idx) => (
              <div 
                key={room.id} 
                id={room.id}
                className="bg-[#FBF7F1] rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow scroll-mt-24"
              >
                <div className={`flex flex-col ${idx % 2 === 1 ? 'md:flex-row-reverse' : 'md:flex-row'}`}>
                  {/* Images Carousel */}
                  <div className="w-full md:w-1/2 flex-shrink-0">
                    <ImageCarousel images={room.images} name={room.name} badge={room.badge} />
                  </div>

                  {/* Content */}
                  <div className="w-full md:w-1/2 p-5 sm:p-8 flex flex-col justify-center">
                    <h3 className="font-serif text-xl sm:text-2xl text-[#2E2A26] mb-2 sm:mb-3">{room.name}</h3>
                    {room.availableFrom && (
                      <span className={`inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full mb-3 self-start border ${room.availableFrom.startsWith("Fully booked") ? "bg-stone-100 border-stone-200 text-[#2E2A26]/55" : "bg-[#C85A38]/10 border-[#C85A38]/20 text-[#C85A38]"}`} data-testid={`availability-${room.id}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${room.availableFrom.startsWith("Fully booked") ? "bg-stone-400" : "bg-[#C85A38]"}`} />
                        {room.availableFrom}
                      </span>
                    )}
                    <p className="text-[#2E2A26]/60 mb-4 text-sm sm:text-base">{room.description}</p>
                    
                    <div className="flex flex-wrap gap-2 mb-4 sm:mb-6">
                      {room.features.map((feature, i) => (
                        <span 
                          key={i}
                          className="inline-flex items-center gap-1.5 bg-[#F4ECE2] text-[#2E2A26]/80 text-xs px-3 py-1.5 rounded-full"
                        >
                          <Check className="w-3 h-3 text-[#8C9A7C]" />
                          {feature}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-2xl sm:text-3xl font-serif text-[#C85A38]"><span className="text-base font-normal">from </span>{formatPrice(room.price)}</p>
                        <p className="text-[#2E2A26]/50 text-xs sm:text-sm">per month · excl. IVA · whole house included</p>
                      </div>
                      <Link to="/#dates" data-testid={`enquire-${room.id}`}>
                        <Button className="bg-[#2E2A26] hover:bg-[#2D3025] text-white rounded-full text-sm">
                          Check dates
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-10 sm:py-12 px-4 sm:px-6 lg:px-8 bg-[#F4ECE2]">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] text-center mb-2">
            What's Included in Every Room
          </h2>
          <p className="text-[#2E2A26]/50 text-sm text-center mb-6 sm:mb-8">…and every rate includes the full environment: coworking space, pool, gym, sky terrace, and community programming.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-6">
            {amenities.map((amenity, idx) => (
              <div key={idx} className="flex items-center gap-3 bg-[#FBF7F1] rounded-xl p-3 sm:p-4">
                <amenity.icon className="w-5 h-5 text-[#C85A38] flex-shrink-0" />
                <span className="text-[#2E2A26]/80 text-xs sm:text-sm">{amenity.text}</span>
              </div>
            ))}
          </div>
          <div className="mt-5 sm:mt-6 text-center">
            <p className="text-[#2E2A26]/60 text-xs sm:text-sm">
              🌙 Quiet hours after 10pm • 🚭 Smoke-free indoor spaces
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#2E2A26] mb-3 sm:mb-4">Found one you like?</h2>
          <p className="text-[#2E2A26]/60 mb-6 text-sm sm:text-base">
            Send Steve your dates and he&apos;ll come back within 24 hours with what&apos;s free — or ask him anything first.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link to="/#dates" data-testid="rooms-check-dates-cta">
              <Button className="bg-[#C85A38] hover:bg-[#A84830] text-white rounded-full px-6 sm:px-8 w-full sm:w-auto">
                Check dates &amp; rooms
              </Button>
            </Link>
            <a href={`${WHATSAPP}?text=${encodeURIComponent("Hi Steve! I've got a question about the rooms at Savi.")}`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-[#2E2A26] text-[#2E2A26] rounded-full px-6 sm:px-8 w-full sm:w-auto">
                Ask Steve a question
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
};

export default RoomsPage;
